import { FC, useCallback, useState } from 'react'
import { useNavigate } from 'react-router-dom'

import { Box } from '@factorialco/deprecated-design-system'
import {
  Widget,
  WidgetEmptyState,
  WidgetInboxList,
} from '@factorialco/factorial-one-react/dist/experimental'
import { InboxHomeWidgetDocument, InboxHomeWidgetQuery } from 'generated/resources/graphql'
import isNil from 'lodash/isNil'
import orderBy from 'lodash/orderBy'
import moment from 'moment'

import { useNavigation } from 'modules/core/hooks/useNavigation'
import { track, trackingEvents } from 'modules/core/lib/Tracking'
import { convertDate, stringToDate } from 'modules/core/lib/dates'
import i18n from 'modules/core/lib/i18n'
import { buildWidget } from 'modules/core/lib/widgets'

import { NEW_HOME_WIDGETS, NEW_HOME_WIDGET_ACTION_TYPES } from 'modules/dashboard/lib/events'

import * as styles from './index.vanilla.css'

const useLinkAndTitle = () => {
  const title = i18n.t('new_dashboard.inbox.title')

  const navigate = useNavigate()

  const { navigation } = useNavigation()

  const linkUrl = navigation.inbox.requests.fullPath()

  const handleActionClick = () => {
    track(trackingEvents.dashboard.clickOnHomeWidgetAction, {
      widget: NEW_HOME_WIDGETS.INBOX,
      actionType: NEW_HOME_WIDGET_ACTION_TYPES.BOTTOM_BUTTON,
    })
    navigate(linkUrl)
  }

  const handleUpperRightActionClick = () => {
    track(trackingEvents.dashboard.clickOnHomeWidgetAction, {
      widget: NEW_HOME_WIDGETS.INBOX,
      actionType: NEW_HOME_WIDGET_ACTION_TYPES.UPPER_RIGHT_BUTTON,
    })
  }

  const link = {
    url: linkUrl,
    title,
    onClick: handleUpperRightActionClick,
  }

  return {
    title,
    link,
    handleActionClick,
  }
}
const prepareData = (data: InboxHomeWidgetQuery) => {
  const count = data.notifications.requests.length

  const sortedItems = orderBy(
    data.notifications.requests,
    [
      (request) => isNil(request.dueDate),
      (request) => request.dueDate && moment(request.dueDate).valueOf(),
      (request) => stringToDate(request.resourceCreatedAt, 'backend_datetime').toDate(),
      (request) => request.text,
    ],
    ['asc', 'asc', 'desc', 'asc']
  )

  const items = sortedItems
    .map((item) => ({
      id: item.id,
      title: item.text,
      subtitle: `${item.categoryTitle} · ${convertDate(
        item.resourceCreatedAt,
        'backend_datetime',
        'ui_dmy_long'
      )}`,
      url: item.actionUrl,
      remoteUrl: item.remoteUrl,
    }))
    .slice(0, 10)

  return {
    count,
    items,
  }
}

type PreparedData = ReturnType<typeof prepareData>

const InboxWidgetComponent: FC<{ data: PreparedData; employeeId: number }> = ({
  data: { items, count },
}) => {
  const { link, title, handleActionClick } = useLinkAndTitle()

  const [visibleItemsCount, setVisibleItemsCount] = useState(0)

  const handleVisibleItemsChange = useCallback((visibleItems: unknown[]) => {
    setVisibleItemsCount(visibleItems.length)
  }, [])

  const remainingItemsCount = count - visibleItemsCount

  const buttonLabel =
    remainingItemsCount > 0
      ? i18n.tp('new_dashboard.inbox.see_more', { count: remainingItemsCount })
      : undefined

  const navigate = useNavigate()

  const handleClickItem = (itemId: number | string) => {
    track(trackingEvents.dashboard.clickOnHomeWidgetItem, {
      widget: NEW_HOME_WIDGETS.INBOX,
    })

    const item = items.find((it) => it.id === itemId)

    if (!item) return

    if (item.remoteUrl) {
      return (window.location.href = item.remoteUrl)
    }

    if (item.url) {
      navigate(item.url)
    }
  }

  return (
    <Widget
      header={{
        title,
        link,
        count,
      }}
      action={buttonLabel ? { label: buttonLabel, onClick: handleActionClick } : undefined}
    >
      <Box className={styles.container}>
        <WidgetInboxList
          items={items}
          onClickItem={handleClickItem}
          onVisibleItemsChange={handleVisibleItemsChange}
        />
      </Box>
    </Widget>
  )
}

const EmptyComponent = () => {
  const { link, title } = useLinkAndTitle()

  return (
    <Widget header={{ title, link }}>
      <WidgetEmptyState
        title={i18n.t('new_dashboard.inbox.empty.title')}
        description={i18n.t('new_dashboard.inbox.empty.description')}
        emoji="☕️"
      />
    </Widget>
  )
}

export const buildInboxWidget = () =>
  buildWidget({
    id: 'inbox',
    document: InboxHomeWidgetDocument,
    emptyComponent: () => <EmptyComponent />,
    isEmpty: ({ items }) => !items.length,
    prepareData,
    component: InboxWidgetComponent,
  })
